import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { T, Var } from 'gt-react';
import type {
  McpAuthorizationDescription,
  McpAuthorizationRequest,
} from '@erp/rpc';

import { orpc } from 'src/api/rpc/client';
import {
  AlertContainer as Alert,
  AlertDescription,
} from 'src/components/ui/alert';
import { Button } from 'src/components/ui/button';
import { Spinner } from 'src/components/ui/spinner';
import { AuthErrorMessage } from 'src/pages/(unauthenticated)/AuthErrorMessage';
import { AuthShell } from 'src/pages/(unauthenticated)/AuthShell';
import { EmailCodeStep } from './EmailCodeStep';
import { WriteAccessToggle } from './WriteAccessToggle';
import {
  isLeaving,
  useApproveAuthorization,
  useDenyAuthorization,
} from './useConsentActions';

type ApprovableDescription = Extract<
  McpAuthorizationDescription,
  { status: 'ready' | 'verify_email' }
>;

/**
 * The approve/cancel prompt. For `verify_email` (a password login) approval
 * first needs the 6-digit code emailed to the user's email address.
 */
export function ConsentPrompt({
  request,
  clientName,
  description,
}: {
  request: McpAuthorizationRequest;
  clientName: string;
  description: ApprovableDescription;
}) {
  const [verificationCode, setVerificationCode] = useState('');
  // Stays true through a resend, so the input doesn't vanish mid-entry.
  const [codeSent, setCodeSent] = useState(false);
  // Always starts unticked, even when the client asked for writes: granting
  // them must be the user's own act.
  const [allowWrites, setAllowWrites] = useState(false);
  const approve = useApproveAuthorization();
  const deny = useDenyAuthorization();
  const sendCode = useMutation({
    ...orpc.mcpOAuth.sendEmailVerificationCode.mutationOptions(),
    onSuccess: () => setCodeSent(true),
  });

  const needsCode = description.status === 'verify_email';
  const canApprove = !needsCode || /^\d{6}$/.test(verificationCode);
  const leaving = isLeaving(approve, deny);

  return (
    <AuthShell
      title={
        <T context="Gently MCP consent: heading asking to authorize an AI app">
          Connect <Var>{clientName}</Var> to Gently?
        </T>
      }
      description={
        <T context="Gently MCP consent: who the agent will act as">
          It will act as <Var>{description.userName}</Var> (
          <Var>{description.email}</Var>) in{' '}
          <Var>{description.organizationName}</Var>, with your permissions, and
          can read data you have access to. The connection lasts 30 days.
        </T>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-gray-500 text-center">
          <T context="Gently MCP consent: where the browser returns after approval">
            You'll be sent back to <Var>{description.redirectTarget}</Var>
          </T>
        </p>
        <WriteAccessToggle
          checked={allowWrites}
          onCheckedChange={setAllowWrites}
        />
        {needsCode && (
          <EmailCodeStep
            email={description.email}
            codeSent={codeSent}
            isSending={sendCode.isPending}
            onSend={() => sendCode.mutate(request)}
            code={verificationCode}
            onCodeChange={setVerificationCode}
          />
        )}
        <AuthErrorMessage
          error={sendCode.error ?? approve.error ?? deny.error}
        />
        <Alert className="border-amber-500 bg-amber-50 py-3">
          <AlertDescription className="text-amber-900">
            <T context="Gently MCP consent: warning about links sent by someone else">
              Only approve if you started this from <Var>{clientName}</Var> just
              now. If someone sent you this link, deny it.
            </T>
          </AlertDescription>
        </Alert>
        <Button
          className="w-full"
          disabled={leaving || !canApprove}
          onClick={() =>
            approve.mutate({
              ...request,
              allowWrites,
              ...(needsCode ? { verificationCode } : {}),
            })
          }
        >
          {approve.isPending ? (
            <Spinner className="mx-auto text-white" />
          ) : (
            <T context="Gently MCP consent: approve button">Allow Access</T>
          )}
        </Button>
        <Button
          variant="outline"
          className="w-full"
          disabled={leaving}
          onClick={() => deny.mutate(request)}
        >
          <T context="Gently MCP consent: decline button">Cancel</T>
        </Button>
      </div>
    </AuthShell>
  );
}
